import React, { useState, useEffect } from "react";
import {
    IconButton,
    Switch,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    CircularProgress,
    Paper,
    Tooltip
} from '@mui/material'
import moment from "moment";
import {Select, MenuItem} from "@mui/material";
import DataService from "../DataService";
import DeleteIcon from '@mui/icons-material/Delete';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import VisuaSearchTagModal from "./VisuaSearchTagModal";
import Toast from "./Toast";


function SelectThreshold(props) {
    const {value, change, row} = props;
    const [activeSelect, setActiveSelect] = useState(value)

    return (
        <Select
            sx={{
                ".MuiSelect-select": {
                    padding: "8px 14px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                    border: 0,
                    borderBottom: "1px solid #484850",
                    borderRadius: "0"
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: 0,
                    borderBottom: "1px solid #484850",
                    borderRadius: "0"
                },
            }}
            id="demo-simple-select-standard"
            value={activeSelect}
            onChange={
                (event) => {
                    const value = event.target.value;
                    setActiveSelect(event.target.value);
                    const {id} = row;
                    DataService.putText(id, value).then((result) => {
                        if (result) {
                            change(result.items);
                        }
                    });
                }
            }
        >
            <MenuItem value={0.2}>0.2</MenuItem>
            <MenuItem value={0.4}>0.4</MenuItem>
            <MenuItem value={0.5}>0.5</MenuItem>
            <MenuItem value={0.6}>0.6</MenuItem>
            <MenuItem value={0.7}>0.7</MenuItem>
            <MenuItem value={0.8}>0.8</MenuItem>
            <MenuItem value={0.9}>0.9</MenuItem>
            <MenuItem value={1}>1</MenuItem>
        </Select>
    )
}

function ActivationSwitch(props) {
    const {status, row, change} = props;
    const [checked, setChecked] = useState(status)

    const handleChange = (value) => {
        setChecked(Boolean(value))
        const {id, threshold} = row;
        if (value) {
            DataService.putText(id, threshold).then((result) => {
                if (result) {
                    change(result.items);
                }
            });
        } else {
            DataService.deleteText(id).then((result) => {
                if (result) {
                    change(result.items);
                }
            });
        }
    }

    return (
        <Switch checked={checked} value={checked} onChange={
                (e) => {
                    handleChange(e.target.checked)
                }
            }
        />

    )
}


// Helper function to highlight search text
function highlightText(text, searchTerm) {
    if (!searchTerm || !text) {
        return text;
    }
    
    const escapedSearch = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedSearch})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => {
        // Check if this part matches the search term (case-insensitive)
        if (part.toLowerCase() === searchTerm.toLowerCase()) {
            return (
                <mark key={index} style={{ backgroundColor: '#ffeb3b', padding: '2px 0' }}>
                    {part}
                </mark>
            );
        }
        return part;
    });
}

function compareRowsByField(field, a, b) {
    /** @type {Intl.CollatorOptions} */
    const collatorOpts = { sensitivity: 'base', numeric: true };
    switch (field) {
        case 'text':
            return String(a.text ?? '').localeCompare(String(b.text ?? ''), undefined, collatorOpts);
        case 'brand':
            return String(a.brand?.name ?? '').localeCompare(String(b.brand?.name ?? ''), undefined, collatorOpts);
        case 'context': {
            const hasA = Boolean(a?.verification);
            const hasB = Boolean(b?.verification);
            return (hasA ? 1 : 0) - (hasB ? 1 : 0);
        }
        default: {
            const va = a[field];
            const vb = b[field];
            if (va == null && vb == null) return 0;
            if (va == null) return 1;
            if (vb == null) return -1;
            if (typeof va === 'number' && typeof vb === 'number') return va - vb;
            return String(va).localeCompare(String(vb), undefined, collatorOpts);
        }
    }
}

export default function TextTable(
    {
        items = [],
        setItems,
        loading,
        setLoading,
        change,
        searchText = '',
        showingContext = false,
    }
) {

    const [tableOption, setTableOption] = useState({
        pageSize: 50,
        page: 0,
    })
    const [showWarning, setShowWarning] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [openTagModal, setOpenTagModal] = useState(false);
    const [toastData, setToastData] = useState({
        open: false,
        severity: 'info',
        message: '',
        duration: 0,
    });
    const [sort, setSort] = useState(
        /** @type {{ orderBy: string | null; order: 'asc' | 'desc' }} */ ({
            orderBy: null,
            order: 'asc',
        })
    );

    // Reset page to 0 when search text changes
    useEffect(() => {
        setTableOption(prev => ({ ...prev, page: 0 }));
    }, [searchText]);

    useEffect(() => {
        setTableOption(prev => ({ ...prev, page: 0 }));
    }, [sort.orderBy]);

    const columns = [
        { field: 'id', headerName: 'ID', width: 90 },
        {
            field: 'text',
            headerName: 'Text library',
            minWidth: 200,
            flex: 1,
            renderCell: (params) => {
                return highlightText(params.row.text, searchText);
            }
        },
        {
            field: 'brand',
            headerName: 'Brand',
            width: 200,
            renderCell: (params) => {
                const brandName = params.row?.brand?.name;
                return brandName ? highlightText(brandName, searchText) : null;
            }
        },
        {
            field: 'context',
            headerName: 'Context',
            width: 88,
            align: 'center',
            renderCell: (params) => {
                const hasVerification = Boolean(params.row?.verification);
                return (
                    <Checkbox
                        checked={hasVerification}
                        disabled
                        size="small"
                        inputProps={{ 'aria-label': hasVerification ? 'Context verification present' : 'No context verification' }}
                        sx={{
                            p: 0.5,
                            pointerEvents: 'none',
                            '&.Mui-disabled': { color: 'inherit' },
                        }}
                    />
                );
            }
        },
        {
            field: 'threshold',
            headerName: 'Confidence',
            renderCell: (params) => {
                const { row } =params;
                const { threshold } = row;
                return (
                    <SelectThreshold value={threshold} change={change} {...params} />
                )
            },
            type: 'number',
            width: 150,
            align: 'center'
        },
        {
            field: 'dateEntered',
            headerName: 'Date Entered',
            width: 150,
            renderCell: (params) => {
                const { row } =params;
                const { dateEntered } = row;
                return moment(dateEntered, 'YYYY-MM-DD').format("D/M/Y");
            }
        },
        {
            field: 'status',
            headerName: 'Toggle',
            renderCell: (params) => {
               const {row} = params;
               const {status} = row;
               return (
                   <ActivationSwitch status={status === 1} change={change} row={row}/>
               )
            }
        },
        {
            field: 'meta',
            headerName: 'Meta',
            width: 80,
            align: 'center',
            sortable: false,
            renderCell: (params) => {
                const {row} = params;
                const hasMeta = !!row?.meta && Object.keys(row.meta).length > 0;
                return (
                    <Tooltip title={hasMeta ? 'Edit meta tags' : 'Add meta tags'}>
                        <IconButton
                            color={hasMeta ? 'primary' : 'default'}
                            onClick={() => {
                                setSelectedItem(row);
                                setOpenTagModal(true);
                            }}
                        >
                            <LocalOfferIcon />
                        </IconButton>
                    </Tooltip>
                );
            }
        },
        {
            field: 'delete',
            headerName: 'Delete',
            sortable: false,
            renderCell: (params) => {
                const {row} = params;

                return (
                    <IconButton color={'error'}
                        onClick={() => setShowWarning(row.text)}
                    >
                        <DeleteIcon/>
                    </IconButton>
                );
            }
        }
    ];
    const visibleColumns = columns.filter(c => c.field !== 'id' && (showingContext || c.field !== 'context'));

    const deleteElem = () => {
        const idEl = items.find(el => el.text === showWarning)?.id;
        if (idEl) {
            setLoading(true);
            DataService
                .deleteText(idEl, true)
                .then((resp) => {
                    setItems(resp.items);
                })
                .finally(() => setLoading(false))
        }

    }

    const saveNewMeta = (meta) => {
        setOpenTagModal(false);
        if (!selectedItem?.id) {
            return;
        }

        DataService.putTextItemMeta(selectedItem.id, meta)
            .then((response) => {
                const status = response?.status || 0;
                if (status === 200) {
                    setToastData({
                        duration: 5000,
                        severity: 'success',
                        message: "Thank you. The new tag has been applied.",
                        open: true,
                    });

                    const copiedItems = [...items];
                    const indexItemToUpdate = copiedItems.findIndex(el => el.id === selectedItem.id);
                    if (indexItemToUpdate !== -1) {
                        copiedItems[indexItemToUpdate] = {
                            ...copiedItems[indexItemToUpdate],
                            meta,
                        };
                        change(copiedItems);
                    }
                } else {
                    setToastData({
                        duration: 5000,
                        severity: 'error',
                        message: "Something went wrong. No tag applied",
                        open: true,
                    });
                }
            })
            .catch(() => {
                setToastData({
                    duration: 5000,
                    severity: 'error',
                    message: 'XHR Failed for putTextItemMeta',
                    open: true,
                });
            });
    };

    const sortedItems = React.useMemo(() => {
        if (!sort.orderBy) {
            return items;
        }
        const dir = sort.order === 'asc' ? 1 : -1;
        return [...items].sort((a, b) => dir * compareRowsByField(sort.orderBy, a, b));
    }, [items, sort.orderBy, sort.order]);

    const paginatedItems = React.useMemo(() => {
        const start = tableOption.page * tableOption.pageSize;
        const end = start + tableOption.pageSize;
        return sortedItems.slice(start, end);
    }, [sortedItems, tableOption.page, tableOption.pageSize]);

    const handleRequestSort = (field) => {
        setSort((prev) => {
            if (prev.orderBy === field) {
                if (prev.order === 'asc') {
                    return { orderBy: field, order: 'desc' };
                }
                return { orderBy: null, order: 'asc' };
            }
            return { orderBy: field, order: 'asc' };
        });
    };

    return (
        <>
            <Dialog
                open={showWarning !== ''}
                onClose={() => setShowWarning('')}
            >
                <DialogTitle>
                    Warning
                </DialogTitle>
                <DialogContent>
                    Are you sure you want to delete the text "{showWarning}" from your library?
                </DialogContent>
                <DialogActions>
                    <Button
                        color={'error'}
                        onClick={() => {
                            deleteElem();
                            setShowWarning('');
                        }}
                    >
                        Yes
                    </Button>
                    <Button
                        color={'secondary'}
                        onClick={() => setShowWarning('')}
                    >
                        Undo
                    </Button>
                </DialogActions>
            </Dialog>
            <TableContainer component={Paper}
                                    sx={{
                                        height: "calc(100vh - 72px - 32px - 60px - 32px - 50px - 10px - 70px - 48px)",
                                        overflow: 'auto',
                                    }}
            
            >
                {loading && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                        <CircularProgress size={24} />
                    </div>
                )}
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {visibleColumns.map((col) => {
                                const sortable = col.sortable !== false;
                                return (
                                    <TableCell
                                        key={col.field}
                                        style={{ width: col.width }}
                                        sortDirection={
                                            sort.orderBy === col.field ? sort.order : false
                                        }
                                    >
                                        {sortable ? (
                                            <TableSortLabel
                                                active={sort.orderBy === col.field}
                                                direction={
                                                    sort.orderBy === col.field ? sort.order : 'asc'
                                                }
                                                onClick={() => handleRequestSort(col.field)}
                                            >
                                                {col.headerName}
                                            </TableSortLabel>
                                        ) : (
                                            col.headerName
                                        )}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody

                    >
                        {paginatedItems.map((row) => (
                            <TableRow key={row.id} hover>
                                {visibleColumns.map((col) => {
                                    const alignValue = col.align === 'center' ? 'center' : col.align === 'right' ? 'right' : col.align === 'justify' ? 'justify' : 'left';
                                    if (col.renderCell) {
                                        const params = { row };
                                        return (
                                            <TableCell key={col.field} align={alignValue}>
                                                {col.renderCell(params)}
                                            </TableCell>
                                        );
                                    }
                                    const value = row[col.field];
                                    return (
                                        <TableCell key={col.field} align={alignValue}>
                                            {value}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                        {paginatedItems.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={visibleColumns.length} align="center">
                                    No data
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>

            </TableContainer>
            <TablePagination
                    component="div"
                    count={items.length}
                    page={tableOption.page}
                    onPageChange={(_, newPage) =>
                        setTableOption(prev => ({ ...prev, page: newPage }))
                    }
                    rowsPerPage={tableOption.pageSize}
                    onRowsPerPageChange={(e) => {
                        const newSize = parseInt(e.target.value, 10);
                        setTableOption(prev => ({ ...prev, pageSize: newSize, page: 0 }));
                    }}
                    rowsPerPageOptions={[10, 50, 100]}
                />
            <VisuaSearchTagModal
                open={openTagModal}
                handleClose={_ => setOpenTagModal(false)}
                handleSave={saveNewMeta}
                item={selectedItem}
            />
            <Toast
                open={toastData.open}
                message={toastData.message}
                severity={toastData.severity}
                autoHideDuration={toastData.duration}
                handleClose={_ => setToastData(
                    prevState => {
                        return {
                            ...prevState,
                            open: false,
                        }
                    }
                )}
            />
        </>

    );
}
